import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { galleryItems, photos } from "@/data/site";
import { PageHero } from "@/components/site/PageHero";
import { Lightbox } from "@/components/site/Lightbox";
import { CtaBanner } from "@/components/site/CtaBanner";
import { SEO } from "@/components/site/SEO";

const cats = ["tutti", "casa", "camere", "cucina", "spazi", "dettagli", "territorio"];
const catLabels = {
  it: { tutti: "Tutti", casa: "La Casa", camere: "Le Camere", cucina: "La Cucina", spazi: "Gli Spazi", dettagli: "I Dettagli", territorio: "Il Territorio" },
  en: { tutti: "All", casa: "The House", camere: "Rooms", cucina: "Kitchen", spazi: "Spaces", dettagli: "Details", territorio: "The Area" },
  fr: { tutti: "Toutes", casa: "La Maison", camere: "Chambres", cucina: "Cuisine", spazi: "Espaces", dettagli: "Détails", territorio: "Les Alentours" },
  de: { tutti: "Alle", casa: "Das Haus", camere: "Zimmer", cucina: "Küche", spazi: "Bereiche", dettagli: "Details", territorio: "Die Umgebung" },
  es: { tutti: "Todas", casa: "La Casa", camere: "Habitaciones", cucina: "Cocina", spazi: "Espacios", dettagli: "Detalles", territorio: "El Territorio" },
};

export default function GalleryPage() {
  const { t, lang } = useLang();
  const [cat, setCat] = useState("tutti");
  const [open, setOpen] = useState(null);

  const items = useMemo(
    () => galleryItems.filter((g) => cat === "tutti" || g.cat === cat).map((g) => ({ ...g, alt: `${catLabels[lang][g.cat]} — Casa Vacanze Margherita` })),
    [cat, lang]
  );

  return (
    <>
      <SEO title={t.pages.gallery.seo} description={t.pages.gallery.desc} path="/gallery" />
      <PageHero image={photos.cucinaTavola} eyebrow={t.pages.gallery.eyebrow} title={t.pages.gallery.title} testid="gallery-hero" />

      <section className="py-14 sm:py-20" data-testid="gallery-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-wrap gap-2" role="tablist">
            {cats.map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={cat === c}
                data-testid={`gallery-filter-${c}`}
                onClick={() => { setCat(c); setOpen(null); }}
                className={`rounded-full px-5 py-2.5 text-sm font-bold transition-all duration-200 ${
                  cat === c ? "bg-terracotta text-sand shadow-lg shadow-terracotta/25" : "border border-border bg-white text-ink-muted hover:text-ink"
                }`}
              >
                {catLabels[lang][c]}
              </button>
            ))}
          </div>

          <motion.div layout className="columns-2 gap-4 md:columns-3 [&>*]:mb-4">
            {items.map((item, i) => (
              <motion.button
                layout
                key={item.src}
                data-testid={`gallery-item-${i}`}
                onClick={() => setOpen(i)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(i * 0.03, 0.4) }}
                className="group relative block w-full overflow-hidden rounded-2xl"
              >
                <img src={item.src} alt={item.alt} loading="lazy" className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute inset-0 bg-mare/0 transition-colors duration-300 group-hover:bg-mare/20" />
              </motion.button>
            ))}
          </motion.div>
        </div>
      </section>

      <Lightbox images={items} index={open} onClose={() => setOpen(null)} onNavigate={setOpen} />
      <CtaBanner />
    </>
  );
}
